import { useMemo } from 'react'
import type { PropsWithChildren, ReactNode } from 'react'

import findIndex from 'lodash/findIndex'
import moment from 'moment'

import { dateToString } from 'modules/core/lib/dates'

import type { MunchedData } from 'modules/calendar/lib/munchers'
import type { CalendarViewEvent } from 'modules/calendar/lib/munchers/types'

import type { CalendarEmployeeLookup } from 'modules/calendar/lib/calendarEmployeeData'

import DateCellWrapper from './components/DateCellWrapper'
import MonthDate from './components/MonthDate'
import MonthEvent from './components/MonthEvent'
import LeaveStore from './components/MonthEvent/LeaveStore'

interface EventProps {
  event: CalendarViewEvent
}
interface CellWrapperProps {
  value: Date
  range: Date[]
}
interface DateHeaderProps {
  date: moment.Moment
  isOffRange: boolean
}
interface ReturnType {
  event: (eventProps: EventProps) => ReactNode
  dateCellWrapper: (cellWrapperProps: PropsWithChildren<CellWrapperProps>) => ReactNode
  month: {
    dateHeader: (dateHeaderProps: DateHeaderProps) => ReactNode
  }
}
interface Props {
  munchedData: MunchedData
  canEditBankHolidays: boolean
  employeeLookup: CalendarEmployeeLookup
}

const useCalendarComponents = ({
  munchedData,
  canEditBankHolidays,
  employeeLookup,
}: Props): ReturnType => {
  const leaveStore = useMemo(() => new LeaveStore(), [])
  const { leaves: groupedLeaves, bubbles, companyHolidays } = munchedData

  return {
    event: function renderMonthEvent({ event }: EventProps) {
      return <MonthEvent event={event} leaveStore={leaveStore} employeeLookup={employeeLookup} />
    },
    dateCellWrapper: function renderCellWrapper({
      children,
      value,
      range,
    }: PropsWithChildren<CellWrapperProps>) {
      const index = findIndex(range, (d) => value === d)
      const isFirst = index === 0
      const isLast = index === range.length - 1
      const day = moment(value)
      const events = bubbles[dateToString(day, 'backend')] || []
      const holidays = companyHolidays[dateToString(day, 'backend')] || []
      const leavesForDate = groupedLeaves[dateToString(day, 'backend')] || []

      return (
        <DateCellWrapper
          day={day}
          holidays={holidays}
          events={events}
          leaves={leavesForDate}
          isFirst={isFirst}
          isLast={isLast}
          canEditBankHolidays={canEditBankHolidays}
          employeeLookup={employeeLookup}
        >
          {children}
        </DateCellWrapper>
      )
    },
    month: {
      dateHeader: function renderDateHeader({ date, isOffRange }: DateHeaderProps) {
        const day = moment(date)
        const events = bubbles[dateToString(day, 'backend')] || []
        const holidays = companyHolidays[dateToString(day, 'backend')] || []

        return (
          <MonthDate day={day} events={events} holidays={holidays} isAnotherMonth={isOffRange} />
        )
      },
    },
  }
}

export default useCalendarComponents
